import { useState, useCallback, useEffect, useRef } from 'react';
import { classService } from '@/school-management/services/class';
import { GroupReportItem } from '@/school-management/types/response';
import { FilterValue } from '@/components/school-management/BimloaFilter/BimloaFilter';
import {
  buildGroupsReportQueryParams,
  CLASSES_REPORT_PAGE_SIZE,
  ClassFiltersRecord,
} from './buildGroupsReportQueryParams';

/**
 * Loads all pages of the groups report for a single teacher (same endpoint as admin).
 */
export function useTeacherClassesReportAggregate(options: {
  organizationId: string | undefined;
  teacherUserId: string | undefined;
  translateForGradeMapper: (key: string) => string;
  failedToFetchMessage: string;
  enabled?: boolean;
  classFilters?: Record<string, FilterValue>;
}) {
  const {
    organizationId,
    teacherUserId,
    translateForGradeMapper,
    failedToFetchMessage,
    enabled = true,
    classFilters,
  } = options;

  const [items, setItems] = useState<GroupReportItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const fetchAll = useCallback(async () => {
    if (!enabled) {
      setItems([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    if (!organizationId || !teacherUserId) {
      setItems([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    const aggregated: GroupReportItem[] = [];
    const base = buildGroupsReportQueryParams({
      page: 1,
      pageSize: CLASSES_REPORT_PAGE_SIZE,
      sortByUi: 'name',
      sortDirection: 'asc',
      classFilters: classFilters as ClassFiltersRecord,
      lockedTeacherIds: [teacherUserId],
      translateForGradeMapper,
    });

    try {
      let page = 0;
      let hasNextPage = true;

      while (hasNextPage) {
        page += 1;
        const response = await classService.getAllClasses(organizationId, {
          ...base,
          page,
        });
        if (requestId !== requestIdRef.current) {
          return;
        }
        if (response?.data?.items?.length) {
          aggregated.push(...response.data.items);
        }
        hasNextPage = !!response?.data?.hasNextPage;
      }

      if (requestId === requestIdRef.current) {
        setItems(aggregated);
      }
    } catch (err: unknown) {
      if (requestId === requestIdRef.current) {
        const message = (err as { message?: string })?.message || failedToFetchMessage;
        setError(message);
        setItems([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    enabled,
    organizationId,
    teacherUserId,
    translateForGradeMapper,
    failedToFetchMessage,
    classFilters,
  ]);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    fetchAll();
  }, [enabled, fetchAll]);

  return {
    rawItems: items,
    isLoading,
    error,
    refetch: fetchAll,
  };
}
